import React, { useState, useEffect, useCallback } from 'react';
import { FaGithub, FaChevronLeft, FaChevronRight, FaTimes, FaPlay } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

type ProjectCategory = 'ai' | 'web' | 'mobile' | 'game';

const FILTERS: ('all' | ProjectCategory)[] = ['all', 'game', 'ai', 'web', 'mobile'];

interface Project {
  titleKey: string;
  category: ProjectCategory;
  descriptionKey: string;
  images: string[];
  githubLink: string;
  liveLink?: string;
  isPublic: boolean;
  technologies?: string[];
}

/**
 * Game projects lead, then AI → Full-Stack (web, then mobile), with the
 * strongest work first inside each group.
 */
const projects: Project[] = [
  // ===== GAME DEVELOPMENT =====
  {
    // 3D game · Three.js + custom GLSL + bloom post-processing
    titleKey: 'projects.items.neondrift.title',
    category: 'game',
    descriptionKey: 'projects.items.neondrift.description',
    images: ["/projects/neon-drift/1.jpg", "/projects/neon-drift/2.jpg", "/projects/neon-drift/3.jpg", "/projects/neon-drift/4.jpg"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    liveLink: "/games/neon-drift/",
    isPublic: false,
    technologies: ["Three.js", "TypeScript", "WebGL", "GLSL Shaders", "Bloom Post-processing", "InstancedMesh", "Web Audio API", "Vite"]
  },
  {
    // 3D game · Babylon.js, procedural world + custom character controller
    titleKey: 'projects.items.skyforge.title',
    category: 'game',
    descriptionKey: 'projects.items.skyforge.description',
    images: ["/projects/skyforge/1.jpg", "/projects/skyforge/2.jpg", "/projects/skyforge/3.jpg", "/projects/skyforge/4.jpg"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    liveLink: "/games/skyforge/",
    isPublic: false,
    technologies: ["Babylon.js", "TypeScript", "WebGL", "Procedural Generation", "Shadows & Glow", "Particle Systems", "Web Audio API", "Vite"]
  },
  {
    // 3D game · React Three Fiber + Rapier rigid-body physics
    titleKey: 'projects.items.orbitalvault.title',
    category: 'game',
    descriptionKey: 'projects.items.orbitalvault.description',
    images: ["/projects/orbital-vault/1.jpg", "/projects/orbital-vault/2.jpg", "/projects/orbital-vault/3.jpg", "/projects/orbital-vault/4.jpg"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    liveLink: "/games/orbital-vault/",
    isPublic: false,
    technologies: ["React", "React Three Fiber", "Three.js", "Rapier Physics", "Drei", "Postprocessing", "Zustand", "TypeScript"]
  },

  // ===== AI & AUTOMATION =====
  {
    // Cutting-edge AI: RAG + multi-agent + LangChain/LangGraph/Langfuse
    titleKey: 'projects.items.aicoach.title',
    category: 'ai',
    descriptionKey: 'projects.items.aicoach.description',
    images: ["/projects/aicoach/1.png", "/projects/aicoach/2.png", "/projects/aicoach/3.png", "/projects/aicoach/4.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["LangChain", "LangGraph", "Langfuse", "RAG", "OpenAI", "Multi-Agent", "Python", "Next.js"]
  },
  {
    // Multimodal AI: image + audio + video understanding (vision/ASR/VLM) — not just transcription
    titleKey: 'projects.items.multimodal.title',
    category: 'ai',
    descriptionKey: 'projects.items.multimodal.description',
    images: ["/projects/multimodal/1.png", "/projects/multimodal/2.png", "/projects/multimodal/3.png", "/projects/multimodal/4.png", "/projects/multimodal/5.png", "/projects/multimodal/6.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Python", "OpenAI Vision", "Whisper", "FFmpeg", "Gemini", "FastAPI", "Next.js", "Multimodal AI"]
  },
  {
    // Modern workflow automation
    titleKey: 'projects.items.n8n.title',
    category: 'ai',
    descriptionKey: 'projects.items.n8n.description',
    images: ["/projects/n8n/1.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["n8n", "Webhooks", "REST APIs", "Slack", "PostgreSQL", "Cron"]
  },

  // ===== FULL-STACK — WEB =====
  {
    // ASTU 2025 top-project recognition · microservices · most polished (19 screenshots)
    titleKey: 'projects.items.ethiopass.title',
    category: 'web',
    descriptionKey: 'projects.items.ethiopass.description',
    images: [
      "/projects/ethiopass/1.png",
      "/projects/ethiopass/2.png",
      "/projects/ethiopass/3.png",
      "/projects/ethiopass/4.png",
      "/projects/ethiopass/5.png",
      "/projects/ethiopass/6.png",
      "/projects/ethiopass/7.png",
      "/projects/ethiopass/8.png",
      "/projects/ethiopass/9.png",
      "/projects/ethiopass/10.png",
      "/projects/ethiopass/11.png",
      "/projects/ethiopass/12.png",
      "/projects/ethiopass/13.png",
      "/projects/ethiopass/14.png",
      "/projects/ethiopass/15.png",
      "/projects/ethiopass/16.png",
      "/projects/ethiopass/17.png",
      "/projects/ethiopass/18.png",
      "/projects/ethiopass/19.png"
    ],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Next Js", "NEST.JS", "Microservice", "Prisma", "MySQL", "JWT", "TailwindCSS", "TypeScript"]
  },
  {
    // Full-stack e-commerce with payments
    titleKey: 'projects.items.digitalds.title',
    category: 'web',
    descriptionKey: 'projects.items.digitalds.description',
    images: ["/projects/digitalds/1.png", "/projects/digitalds/2.png", "/projects/digitalds/3.png", "/projects/digitalds/4.png", "/projects/digitalds/5.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Next.js", "Node.js", "Express", "MongoDB", "Stripe", "TailwindCSS", "TypeScript"]
  },
  {
    // Robust LMS full-stack
    titleKey: 'projects.items.elearning.title',
    category: 'web',
    descriptionKey: 'projects.items.elearning.description',
    images: [
      "/projects/elearning/1.png",
      "/projects/elearning/2.png",
      "/projects/elearning/3.png",
      "/projects/elearning/4.png",
      "/projects/elearning/5.png",
      "/projects/elearning/6.png",
      "/projects/elearning/7.png",
      "/projects/elearning/8.png",
      "/projects/elearning/9.png",
      "/projects/elearning/10.png",
      "/projects/elearning/11.png",
      "/projects/elearning/12.png",
      "/projects/elearning/13.png",
      "/projects/elearning/14.png",
      "/projects/elearning/15.png",
      "/projects/elearning/16.png",
      "/projects/elearning/17.png",
      "/projects/elearning/18.png",
      "/projects/elearning/19.png"
    ],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["React", "Node.js", "Express", "Prisma", "MySQL", "JWT", "TailwindCSS", "TypeScript"]
  },
  {
    // Next.js property management
    titleKey: 'projects.items.bms.title',
    category: 'web',
    descriptionKey: 'projects.items.bms.description',
    images: [
      "/projects/bms/1.png",
      "/projects/bms/2.png",
      "/projects/bms/3.png",
    ],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Next.js", "Node.js", "Express", "Prisma", "MySQL", "JWT", "TailwindCSS", "TypeScript"]
  },
  {
    // Blockchain · Solidity smart contracts
    titleKey: 'projects.items.web3.title',
    category: 'web',
    descriptionKey: 'projects.items.web3.description',
    images: ["/projects/web3/1.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Solidity", "Hardhat", "Ethereum", "Ethers.js", "OpenZeppelin", "TypeScript"]
  },
  {
    // Community admin panel — open source ✓
    titleKey: 'projects.items.csec.title',
    category: 'web',
    descriptionKey: 'projects.items.csec.description',
    images: [
      "/projects/csec-dev/hero.png",
      "/projects/csec-dev/admindashboard.png",
      "/projects/csec-dev/allevents.png",
      "/projects/csec-dev/event.png",
      "/projects/csec-dev/upcoming.png",
      "/projects/csec-dev/addevent.png",
      "/projects/csec-dev/editevent.png",
      "/projects/csec-dev/members.png",
      "/projects/csec-dev/addmember.png",
      "/projects/csec-dev/editmember.png",
      "/projects/csec-dev/account.png",
      "/projects/csec-dev/contactus.png",
      "/projects/csec-dev/adminlogin.png",
      "/projects/csec-dev/login.png"
    ],
    githubLink: "https://github.com/motirebuma/CSEC_DEV_MS",
    isPublic: true,
    technologies: ["React", "Node.js", "Express", "MongoDB", "JWT"]
  },

  // ===== FULL-STACK — MOBILE =====
  {
    // Flutter + Socket.io real-time + Maps + SMS
    titleKey: 'projects.items.ride.title',
    category: 'mobile',
    descriptionKey: 'projects.items.ride.description',
    images: [
      "/projects/ride/1.png",
      "/projects/ride/2.png",
    ],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Flutter", "Node.js", "Express", "MySQL", "Socket.io", "Google Maps", 'SMS', 'JWT', 'Prisma']
  },
  {
    // Flutter cross-platform + scraping pipeline
    titleKey: 'projects.items.whatsnew.title',
    category: 'mobile',
    descriptionKey: 'projects.items.whatsnew.description',
    images: ["/projects/whatsnew/1.png", "/projects/whatsnew/2.png", "/projects/whatsnew/3.png", "/projects/whatsnew/4.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Flutter", "Dart", "Web Scraping", "Node.js", "Cheerio", "Cron", "iOS", "Android"]
  },
  {
    // Flutter + Maps + Node — open source ✓
    titleKey: 'projects.items.tour.title',
    category: 'mobile',
    descriptionKey: 'projects.items.tour.description',
    images: [
      "/projects/tour/1.png",
      "/projects/tour/2.png",
      "/projects/tour/3.png",
      "/projects/tour/5.png",
      "/projects/tour/6.png",
      "/projects/tour/8.png",
      "/projects/tour/9.png",
      "/projects/tour/10.png",
      "/projects/tour/11.png",
    ],
    githubLink: "https://github.com/motirebuma/Flutter-Tourism-App",
    isPublic: true,
    technologies: ["Flutter", "Google Maps", "Node.js", "Express.js", "MySQL", "Firebase"]
  },
  {
    // Flutter utility — geolocation + maps
    titleKey: 'projects.items.nearbygas.title',
    category: 'mobile',
    descriptionKey: 'projects.items.nearbygas.description',
    images: ["/projects/nearbygas/1.png", "/projects/nearbygas/2.png", "/projects/nearbygas/3.png", "/projects/nearbygas/4.png", "/projects/nearbygas/5.png"],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Flutter", "Dart", "Google Maps", "Geolocation", "Firebase", "iOS", "Android"]
  },
  {
    // Flutter logistics
    titleKey: 'projects.items.pickdrop.title',
    category: 'mobile',
    descriptionKey: 'projects.items.pickdrop.description',
    images: [
      "/projects/picanddrop/1.jpg",
      "/projects/picanddrop/2.jpg",
      "/projects/picanddrop/3.jpg",
      "/projects/picanddrop/4.jpg",
      "/projects/picanddrop/5.jpg",
      "/projects/picanddrop/6.jpg",
      "/projects/picanddrop/7.jpg",
      "/projects/picanddrop/8.jpg",
    ],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["Flutter", "Node.js", "Express,js", "MySQL", "Custom Location API"]
  },

  // ===== LEGACY =====
  {
    // Basic web stack — older project
    titleKey: 'projects.items.drugstore.title',
    category: 'web',
    descriptionKey: 'projects.items.drugstore.description',
    images: [
      "/projects/drugstore/1.png"
    ],
    githubLink: "https://github.com/motirebuma/itsprivate",
    isPublic: false,
    technologies: ["HTML", "CSS", "JavaScript", "Php", "MySQL"]
  }
];

const Projects: React.FC = () => {
  const [currentImages, setCurrentImages] = useState<{ [key: number]: number }>({});
  const [selectedProject, setSelectedProject] = useState<{ project: Project; projectIndex: number; currentImageIndex: number } | null>(null);
  const [loadingImages, setLoadingImages] = useState<{ [key: string]: boolean }>({});
  const [modalImageLoading, setModalImageLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | ProjectCategory>('all');
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Keep each project's original index: carousel state is keyed by it.
  const visibleProjects = projects
    .map((project, index) => ({ project, index }))
    .filter(({ project }) => activeFilter === 'all' || project.category === activeFilter);

  const countFor = (filter: 'all' | ProjectCategory) =>
    filter === 'all' ? projects.length : projects.filter(p => p.category === filter).length;

  const getSectionStyles = () => {
    if (theme === 'modern') {
      return 'modern-section py-12 sm:py-16 lg:py-20 min-h-screen relative overflow-hidden';
    }
    return 'py-12 sm:py-16 lg:py-20 min-h-screen relative overflow-hidden';
  };

  const getContainerStyles = () => {
    if (theme === 'modern') {
      return 'max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 flex flex-col items-center justify-center';
    }
    return 'max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 flex flex-col items-center justify-center';
  };

  const getCardStyles = (index: number) => {
    if (theme === 'modern') {
      return 'modern-card sf-mission-card overflow-hidden transition-all duration-500 group relative';
    }
    const base = 'nb-project-card bg-white border-4 sm:border-6 border-black overflow-hidden shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 group transform-gpu relative';
    const tilt = index % 2 === 0 ? 'nb-project-card--left' : 'nb-project-card--right';
    return `${base} ${tilt}`;
  };

  const getProjectTitleStyles = () => {
    if (theme === 'modern') {
      return 'text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold modern-heading mb-2 sm:mb-3 transition-colors';
    }
    return 'text-lg sm:text-xl lg:text-2xl xl:text-3xl font-black text-black mb-2 sm:mb-3 group-hover:text-purple-600 transition-colors';
  };

  const getDescriptionStyles = () => {
    if (theme === 'modern') {
      return 'modern-text text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4';
    }
    return 'text-gray-700 text-xs sm:text-sm lg:text-base leading-relaxed mb-3 sm:mb-4 font-medium';
  };

  const getTechBadgeStyles = () => {
    if (theme === 'modern') {
      return 'sf-tech-chip text-xs px-2 sm:px-3 py-1 sm:py-1.5 transition-colors';
    }
    return 'text-xs font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-yellow-300 text-black border-2 border-black hover:bg-yellow-400 hover:-translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 transform-gpu';
  };

  const getStatusBadgeStyles = (isPublic: boolean) => {
    if (theme === 'modern') {
      return `sf-status-chip text-xs sm:text-sm px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 transition-all ${
        isPublic ? 'sf-status-chip--ok' : 'sf-status-chip--locked'
      }`;
    }
    return `text-xs sm:text-sm font-bold px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-full transition-all border-2 border-black hover:-translate-y-1 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transform-gpu ${
      isPublic ? 'bg-green-300 text-green-800 hover:bg-green-400' : 'bg-red-300 text-red-800 hover:bg-red-400'
    }`;
  };

  const getGithubButtonStyles = () => {
    if (theme === 'modern') {
      return 'modern-button-primary inline-flex items-center gap-2 px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3';
    }
    return 'inline-flex items-center gap-1 sm:gap-2 lg:gap-3 px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 bg-purple-400 border-3 border-black rounded-lg text-xs sm:text-sm font-black hover:-translate-y-2 hover:-translate-x-2 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:rotate-1 transition-all duration-300 transform-gpu';
  };

  const getNavigationButtonStyles = () => {
    if (theme === 'modern') {
      return 'sf-mvp-nav p-2 sm:p-3 transition-all';
    }
    return 'bg-white/90 p-2 sm:p-3 rounded-full border-2 sm:border-3 border-black hover:bg-yellow-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition-all transform-gpu';
  };

  const getDotStyles = (isActive: boolean) => {
    if (theme === 'modern') {
      return `w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
        isActive ? 'bg-blue-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'
      }`;
    }
    return `w-2 h-2 sm:w-3 sm:h-3 rounded-full border-2 border-black transition-all duration-300 hover:-translate-y-1 cursor-pointer transform-gpu ${
      isActive ? 'bg-black scale-125 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white hover:bg-yellow-300'
    }`;
  };

  const getDividerStyles = () => {
    if (theme === 'modern') {
      return 'pt-4 sm:pt-6 border-t border-gray-100';
    }
    return 'pt-4 sm:pt-6 border-t-2 sm:border-t-4 border-black';
  };

  const nextImage = (projectIndex: number) => {
    const imageKey = `project-${projectIndex}`;
    setLoadingImages(prev => ({ ...prev, [imageKey]: true }));
    
    setCurrentImages(prev => ({
      ...prev,
      [projectIndex]: ((prev[projectIndex] || 0) + 1) % projects[projectIndex].images.length
    }));
  };

  const prevImage = (projectIndex: number) => {
    const imageKey = `project-${projectIndex}`;
    setLoadingImages(prev => ({ ...prev, [imageKey]: true }));
    
    setCurrentImages(prev => ({
      ...prev,
      [projectIndex]: ((prev[projectIndex] || 0) - 1 + projects[projectIndex].images.length) % projects[projectIndex].images.length
    }));
  };

  const handleImageLoad = (imageKey: string) => {
    setLoadingImages(prev => ({ ...prev, [imageKey]: false }));
  };

  const handleImageClick = (projectIndex: number, imageIndex: number) => {
    const imageKey = `project-${projectIndex}`;
    setLoadingImages(prev => ({ ...prev, [imageKey]: true }));
    
    setCurrentImages(prev => ({ ...prev, [projectIndex]: imageIndex }));
  };

  // Modal slider functions
  const nextModalImage = useCallback(() => {
    if (selectedProject) {
      setModalImageLoading(true);
      const nextIndex = (selectedProject.currentImageIndex + 1) % selectedProject.project.images.length;
      setSelectedProject({
        ...selectedProject,
        currentImageIndex: nextIndex
      });
    }
  }, [selectedProject]);

  const prevModalImage = useCallback(() => {
    if (selectedProject) {
      setModalImageLoading(true);
      const prevIndex = (selectedProject.currentImageIndex - 1 + selectedProject.project.images.length) % selectedProject.project.images.length;
      setSelectedProject({
        ...selectedProject,
        currentImageIndex: prevIndex
      });
    }
  }, [selectedProject]);

  const goToModalImage = useCallback((imageIndex: number) => {
    if (selectedProject) {
      setModalImageLoading(true);
      setSelectedProject({
        ...selectedProject,
        currentImageIndex: imageIndex
      });
    }
  }, [selectedProject]);

  // Keyboard navigation for modal
  useEffect(() => {
    let scrollPosition = 0;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (selectedProject) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            prevModalImage();
            break;
          case 'ArrowRight':
            event.preventDefault();
            nextModalImage();
            break;
          case 'Escape':
            event.preventDefault();
            setSelectedProject(null);
            break;
        }
      }
    };

    if (selectedProject) {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      if (selectedProject) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollPosition);
      }
    };
  }, [selectedProject, nextModalImage, prevModalImage]);

  const handleModalImageLoad = () => {
    setModalImageLoading(false);
  };

  const getLoadingSpinnerStyles = () => {
    if (theme === 'modern') {
      return 'absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl';
    }
    return 'absolute inset-0 flex items-center justify-center bg-white/90 border-2 border-black rounded-lg';
  };

  const LoadingSpinner = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
    const sizeClasses = {
      sm: 'w-6 h-6',
      md: 'w-8 h-8',
      lg: 'w-12 h-12'
    };

    if (theme === 'modern') {
      return (
        <div className={`${sizeClasses[size]} animate-spin`}>
          <div className="w-full h-full border-3 border-blue-200 border-t-blue-600 rounded-full"></div>
        </div>
      );
    }
    
    return (
      <div className={`${sizeClasses[size]} animate-spin`}>
        <div className="w-full h-full border-3 border-gray-300 border-t-black rounded-full"></div>
      </div>
    );
  };

  const getModalNavigationStyles = () => {
    if (theme === 'modern') {
      return 'bg-white/20 backdrop-blur-md border border-white/30 text-white p-3 sm:p-4 rounded-full hover:bg-white/30 hover:scale-110 transition-all duration-300 shadow-lg';
    }
    return 'bg-white/90 border-3 sm:border-4 border-black text-black p-3 sm:p-4 rounded-full hover:bg-yellow-300 hover:scale-110 hover:-translate-y-1 hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-300 transform-gpu';
  };

  const getModalDotStyles = (isActive: boolean) => {
    if (theme === 'modern') {
      return `w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 border-2 cursor-pointer ${
        isActive 
          ? 'bg-white border-white scale-125 shadow-lg' 
          : 'bg-white/30 border-white/50 hover:bg-white/50 hover:scale-110'
      }`;
    }
    return `w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 sm:border-3 border-white transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,1)] sm:hover:shadow-[3px_3px_0px_0px_rgba(255,255,255,1)] transform-gpu ${
      isActive 
        ? 'bg-yellow-400 scale-125 shadow-[2px_2px_0px_0px_rgba(255,255,255,1)] sm:shadow-[3px_3px_0px_0px_rgba(255,255,255,1)]' 
        : 'bg-white/80 hover:bg-yellow-300'
    }`;
  };

  return (
    <section id="projects" className={getSectionStyles()}>
      {/* Small Decorative Elements - Neobrutalism Theme Only */}
      {theme === 'neobrutalism' && (
        <div className="absolute inset-0 -z-10">
          {/* Very small animated blobs - hidden on mobile for performance */}
          <div className="hidden sm:block absolute top-20 right-20 w-16 h-16 bg-yellow-300 rounded-full mix-blend-multiply filter blur-sm opacity-40 animate-blob"></div>
          <div className="hidden sm:block absolute bottom-40 left-20 w-20 h-20 bg-purple-300 rounded-full mix-blend-multiply filter blur-sm opacity-40 animate-blob animation-delay-2000"></div>
          <div className="hidden md:block absolute top-1/2 left-10 w-12 h-12 bg-pink-300 rounded-full mix-blend-multiply filter blur-sm opacity-35 animate-blob animation-delay-4000"></div>
          <div className="hidden md:block absolute bottom-20 right-1/3 w-18 h-18 bg-blue-300 rounded-full mix-blend-multiply filter blur-sm opacity-30 animate-blob"></div>
          <div className="hidden lg:block absolute top-10 left-1/3 w-14 h-14 bg-green-300 rounded-full mix-blend-multiply filter blur-sm opacity-25 animate-blob animation-delay-3000"></div>
          <div className="hidden lg:block absolute bottom-10 left-1/2 w-10 h-10 bg-orange-300 rounded-full mix-blend-multiply filter blur-sm opacity-20 animate-blob animation-delay-5000"></div>
          
          {/* Additional tiny floating elements - desktop only */}
          <div className="hidden xl:block absolute top-1/4 right-1/4 w-8 h-8 bg-red-300 rounded-full mix-blend-multiply filter blur-sm opacity-15 animate-float"></div>
          <div className="hidden xl:block absolute bottom-1/4 left-1/4 w-8 h-8 bg-indigo-300 rounded-full mix-blend-multiply filter blur-sm opacity-15 animate-float animation-delay-2000"></div>
        </div>
      )}

      {/* Enhanced Image Modal with Slider */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm"
          onClick={() => setSelectedProject(null)}
        >
          {/* Close Button */}
          <button 
            className="absolute top-3 sm:top-6 right-3 sm:right-6 text-white hover:text-gray-300 transition-colors p-2 sm:p-3 rounded-full bg-black/50 hover:bg-black/70 z-10"
            onClick={() => setSelectedProject(null)}
          >
            <FaTimes size={20} className="sm:w-6 sm:h-6" />
          </button>

          {/* Project Title */}
          <div className="absolute top-3 sm:top-6 left-3 sm:left-6 z-10">
            <h3 className="text-white text-sm sm:text-xl font-bold bg-black/50 backdrop-blur-sm px-2 sm:px-4 py-1 sm:py-2 rounded-full max-w-[200px] sm:max-w-none truncate">
              {t(selectedProject.project.titleKey)}
            </h3>
          </div>

          {/* Image Counter */}
          <div className="absolute top-3 sm:top-6 left-1/2 transform -translate-x-1/2 z-10">
            <div className="text-white text-xs sm:text-sm font-medium bg-black/50 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full">
              {selectedProject.currentImageIndex + 1} / {selectedProject.project.images.length}
            </div>
          </div>

          {/* Main Image Container */}
          <div className="relative max-w-5xl max-h-[80vh] w-full flex items-center justify-center">
            <img
              src={selectedProject.project.images[selectedProject.currentImageIndex]}
              alt={`${t(selectedProject.project.titleKey)} - ${t('projects.imageAltSuffix', { index: selectedProject.currentImageIndex + 1 })}`}
              className="max-w-full max-h-full object-contain border-2 sm:border-4 border-white rounded-lg sm:rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              onLoad={handleModalImageLoad}
              onLoadStart={() => setModalImageLoading(true)}
            />
            
            {/* Modal Loading Spinner Overlay */}
            {modalImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm rounded-lg sm:rounded-2xl">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {/* Navigation Arrows (only show if multiple images) */}
            {selectedProject.project.images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevModalImage();
                  }}
                  className={`absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 ${getModalNavigationStyles()}`}
                  disabled={modalImageLoading}
                >
                  <FaChevronLeft size={16} className="sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextModalImage();
                  }}
                  className={`absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 ${getModalNavigationStyles()}`}
                  disabled={modalImageLoading}
                >
                  <FaChevronRight size={16} className="sm:w-5 sm:h-5" />
                </button>
              </>
            )}
          </div>

          {/* Dots Navigation (only show if multiple images) */}
          {selectedProject.project.images.length > 1 && (
            <div className="absolute bottom-3 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3 bg-black/50 backdrop-blur-sm px-3 sm:px-6 py-2 sm:py-3 rounded-full max-w-[90vw] overflow-x-auto">
              {selectedProject.project.images.map((_, imageIndex) => (
                <button
                  key={imageIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToModalImage(imageIndex);
                  }}
                  className={getModalDotStyles(selectedProject.currentImageIndex === imageIndex)}
                  disabled={modalImageLoading}
                />
              ))}
            </div>
          )}

          {/* Keyboard navigation hint - hidden on mobile */}
          {selectedProject.project.images.length > 1 && (
            <div className="hidden sm:block absolute bottom-3 sm:bottom-6 right-3 sm:right-6 text-white/70 text-xs bg-black/30 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full">
              {t('projects.keyboardHint')}
            </div>
          )}
        </div>
      )}

      <div className={getContainerStyles()}>
        {/* Hero Section */}
        {theme === 'modern' ? (
          <header className="sf-projects-header w-full mt-16 sm:mt-20 mb-10 sm:mb-12 lg:mb-14 text-center">
            <div className="sf-projects-header__strip">
              <span className="sf-projects-header__tag">MISSION_ARCHIVE</span>
              <span className="sf-dossier__sep">▌</span>
              <span className="sf-projects-header__id">{String(projects.length).padStart(2, '0')}_FILES_INDEXED</span>
              <span className="sf-dossier__sep">▌</span>
              <span className="sf-projects-header__live">
                <span className="sf-dossier__livedot"></span>
                ARCHIVED
              </span>
            </div>
            <h1 className="sf-projects-header__h1">
              <span className="bg-clip-text text-transparent">{t('projects.title')}</span>
            </h1>
            <p className="sf-projects-header__subtitle">
              <span className="sf-bracket">[</span>
              <span>{t('projects.subtitle')}</span>
              <span className="sf-bracket">]</span>
            </p>
            <div className="sf-projects-header__counter">
              <span>SOURCE <strong>github.com/motirebuma</strong></span>
              <span className="sf-modules__counter-bar"></span>
              <span>FILTER <strong>{t(`projects.filters.${activeFilter}`).toUpperCase()}</strong></span>
              <span className="sf-modules__counter-bar"></span>
              <span className="sf-modules__counter-status">● LIVE FEED</span>
            </div>
          </header>
        ) : (
          <header className="w-full mt-16 sm:mt-20 mb-10 sm:mb-12 lg:mb-14">
            <div className="nb-projects-banner">
              <h1 className="nb-projects-banner__h1">★ {t('projects.title').toUpperCase()} ★</h1>
              <p className="nb-projects-banner__subtitle">► {t('projects.subtitle')} ◄</p>
              <div className="nb-projects-counter">
                <span>★ {projects.length} OPS</span>
                <span>★ {projects.filter(p => p.isPublic).length} OPEN</span>
                <span>★ {projects.filter(p => !p.isPublic).length} CLASSIFIED</span>
              </div>
            </div>
          </header>
        )}

        <div
          className={`${theme === 'modern' ? 'sf-filter' : 'nb-filter'} mb-8 sm:mb-10`}
          role="tablist"
          aria-label={t('projects.title')}
        >
          {FILTERS.map(filter => (
            <button
              key={filter}
              type="button"
              role="tab"
              aria-selected={activeFilter === filter}
              className={`${theme === 'modern' ? 'sf-filter__btn' : 'nb-filter__btn'} ${activeFilter === filter ? 'is-active' : ''}`}
              onClick={() => setActiveFilter(filter)}
            >
              {t(`projects.filters.${filter}`)}
              <span className="filter-count">{countFor(filter)}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 xl:gap-12 max-w-6xl mx-auto w-full">
          {visibleProjects.map(({ project, index }) => (
            <div
              key={project.titleKey}
              className={getCardStyles(index)}
            >
              <div className={`relative aspect-[16/10] sm:aspect-[16/9] overflow-hidden ${theme === 'modern' ? 'sf-mission-viewport' : 'nb-mission-viewport'}`}>
                {/* Theme-specific overlay chrome */}
                {theme === 'modern' ? (
                  <>
                    <span className="sf-mvp-label">VIEWPORT // FRAME&nbsp;<strong>{String((currentImages[index] || 0) + 1).padStart(2, '0')}</strong>/{String(project.images.length).padStart(2, '0')}</span>
                    <span className="sf-mvp-corner sf-mvp-tl"></span>
                    <span className="sf-mvp-corner sf-mvp-tr"></span>
                    <span className="sf-mvp-corner sf-mvp-bl"></span>
                    <span className="sf-mvp-corner sf-mvp-br"></span>
                  </>
                ) : (
                  <>
                    <span className="nb-mvp-tape">★ EXHIBIT_{String(index + 1).padStart(2, '0')} ★</span>
                    <span className={`nb-mvp-stamp ${project.isPublic ? 'nb-mvp-stamp--ok' : 'nb-mvp-stamp--locked'}`}>
                      {project.isPublic ? 'DEPLOYED' : 'CLASSIFIED'}
                    </span>
                  </>
                )}
                <img
                  src={project.images[currentImages[index] || 0]}
                  alt={t(project.titleKey)}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 cursor-pointer"
                  onClick={() => setSelectedProject({
                    project: project,
                    projectIndex: index,
                    currentImageIndex: currentImages[index] || 0
                  })}
                  onLoad={() => handleImageLoad(`project-${index}`)}
                  onLoadStart={() => setLoadingImages(prev => ({ ...prev, [`project-${index}`]: true }))}
                />
                
                {/* Loading Spinner Overlay */}
                {loadingImages[`project-${index}`] && (
                  <div className={getLoadingSpinnerStyles()}>
                    <LoadingSpinner size="md" />
                  </div>
                )}
                
                {project.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage(index);
                      }}
                      className={`absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 ${getNavigationButtonStyles()}`}
                      disabled={loadingImages[`project-${index}`]}
                    >
                      <FaChevronLeft size={14} className="sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage(index);
                      }}
                      className={`absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 ${getNavigationButtonStyles()}`}
                      disabled={loadingImages[`project-${index}`]}
                    >
                      <FaChevronRight size={14} className="sm:w-4 sm:h-4" />
                    </button>
                    <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-2">
                      {project.images.map((_, imageIndex) => (
                        <button
                          key={imageIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(index, imageIndex);
                          }}
                          className={getDotStyles((currentImages[index] || 0) === imageIndex)}
                          disabled={loadingImages[`project-${index}`]}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <div className="p-3 sm:p-4 lg:p-6 xl:p-8 space-y-3 sm:space-y-4 lg:space-y-6">
                <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                  {theme === 'modern' ? (
                    <div className="sf-mission-tag">
                      <span className="sf-mission-tag__code">MISSION_{String(index + 1).padStart(2, '0')}</span>
                      <span className="sf-mission-tag__sep">/</span>
                      <span className={`sf-mission-tag__status ${project.isPublic ? 'sf-mission-tag__status--ok' : 'sf-mission-tag__status--locked'}`}>
                        {project.isPublic ? 'OPEN_SOURCE' : 'CLASSIFIED'}
                      </span>
                    </div>
                  ) : (
                    <div className="nb-mission-tag">
                      <span className="nb-mission-tag__code">MISSION_{String(index + 1).padStart(2, '0')}</span>
                      <span className={`nb-mission-tag__status ${project.isPublic ? 'nb-mission-tag__status--ok' : 'nb-mission-tag__status--locked'}`}>
                        {project.isPublic ? 'OPEN★SOURCE' : 'CLASSIFIED'}
                      </span>
                    </div>
                  )}
                  <h3 className={getProjectTitleStyles()}>{t(project.titleKey)}</h3>
                  <p className={getDescriptionStyles()}>{t(project.descriptionKey)}</p>

                  {/* Technologies */}
                  {project.technologies && (
                    <div>
                      {theme === 'modern' && (
                        <div className="sf-stack-label">[ STACK ]</div>
                      )}
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {project.technologies.map((tech, techIndex) => (
                          <span key={techIndex} dir="auto" className={getTechBadgeStyles()}>{tech}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className={`flex flex-row flex-wrap items-center justify-between gap-3 sm:gap-4 ${getDividerStyles()}`}>
                  <span className={getStatusBadgeStyles(project.isPublic)}>
                    {project.isPublic ? t('projects.statuses.public') : t('projects.statuses.private')}
                  </span>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {project.liveLink && (
                    <a
                      href={project.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={getGithubButtonStyles()}
                    >
                      <FaPlay size={12} className="sm:w-3.5 sm:h-3.5" />
                      <span className="text-xs sm:text-sm lg:text-base">
                        {theme === 'modern' ? 'LAUNCH' : t('projects.play')}
                      </span>
                    </a>
                  )}
                  {project.isPublic && (
                    <a
                      href={project.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={getGithubButtonStyles()}
                    >
                      <FaGithub size={14} className="sm:w-4 sm:h-4" />
                      <span className="text-xs sm:text-sm lg:text-base">
                        {theme === 'modern' ? 'ACCESS_REPO' : t('projects.viewCode')}
                      </span>
                    </a>
                  )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects; 